import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api/client";
import { forgotPasswordSchema, zodFieldErrors } from "@/lib/auth-validations";
import { requestPasswordReset } from "@/services/auth";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromLogin =
    typeof location.state === "object" &&
    location.state &&
    "email" in location.state &&
    typeof location.state.email === "string"
      ? location.state.email
      : "";
  const [email, setEmail] = useState(emailFromLogin);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed.error));
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      await requestPasswordReset({ email: parsed.data.email });
      navigate("/reset-password", { state: { email: parsed.data.email } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
      } else {
        setError("Unable to send the reset code");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      title="Forgot password"
      description="Enter your email and we’ll send a 4-digit code to reset your password."
    >
      <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          placeholder="you@partner.com"
        />
        <Button type="submit" className="w-full" loading={loading}>
          Send code
        </Button>
        <p className="text-center text-sm text-muted">
          <Link to="/login" className="font-medium text-brand hover:text-brand-strong">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthSplitLayout>
  );
}
